import { CellContext } from "@tanstack/react-table";
import { Button } from "@bcc/components/ui/button";
import { Link, Link2Off, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { VariantWithProductData } from "../types";
import {
  setActionsCellTestId,
  setActionsCollapsedButtonTestId,
  setActionsDisconnectButtonTestId,
  setActionsMapProductButtonTestId,
} from "../products-table.utils";

interface ActionsCellProps {
  onDisconnect?: () => void;
  onMapProduct?: () => void;
  disconnectDisabled?: boolean;
  mapProductDisabled?: boolean;
  isCollapsed?: boolean;
  onExpandActions?: () => void;
}

export const ActionsCellMarkup = (props: CellContext<VariantWithProductData, unknown> & ActionsCellProps) => {
  const {
    onDisconnect,
    onMapProduct,
    disconnectDisabled = false,
    mapProductDisabled = false,
    isCollapsed = false,
    onExpandActions,
  } = props;
  const { t } = useTranslation();

  // Multi-bag product in collapsed state: show a generic "Actions" button
  // that expands the product rows to reveal per-variant action buttons.
  if (isCollapsed) {
    return (
      <div className="flex justify-end gap-2" data-testid={setActionsCellTestId(props.row.index)}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onExpandActions}
          className="text-muted-foreground hover:text-foreground"
          data-testid={setActionsCollapsedButtonTestId(props.row.index)}
        >
          <MoreHorizontal className="mr-2 h-4 w-4" />
          {t("views.productsTable.actions.collapsedActions", {
            defaultValue: "Actions",
          })}
        </Button>
      </div>
    );
  }

  // Nothing to show — collapse to an empty cell for consistent spacing
  if (!onDisconnect && !onMapProduct) {
    return <div data-testid={setActionsCellTestId(props.row.index)} />;
  }

  return (
    <div className="flex justify-end gap-2" data-testid={setActionsCellTestId(props.row.index)}>
      {onDisconnect && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDisconnect}
          className="min-w-[9rem] text-destructive hover:text-destructive/80"
          disabled={disconnectDisabled}
          data-testid={setActionsDisconnectButtonTestId(props.row.index)}
        >
          <Link2Off className="mr-2 h-4 w-4" />
          {t("views.productsTable.actions.disconnect")}
        </Button>
      )}
      {onMapProduct && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onMapProduct}
          className="min-w-[9rem] text-muted-foreground hover:text-foreground"
          disabled={mapProductDisabled}
          data-testid={setActionsMapProductButtonTestId(props.row.index)}
        >
          <Link className="mr-2 h-4 w-4" />
          {t("views.productsTable.actions.mapProduct")}
        </Button>
      )}
    </div>
  );
};
